import { customComponents } from "hec/builder";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useIntl } from "react-intl";

import { type BuilderContent, Content } from "@builder.io/sdk-react";

import { Button } from "~/components/ui/button";
import Lock from "~/hec/assets/icons/lock";
import { GuardedBuilderLink } from "~/hec/builder/guarded-builder-link";
import { ModelMap } from "~/hec/builder/model-map";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/hec/components/ui/accordion";
import { Badge } from "~/hec/components/ui/badge";
import { useBuilder } from "~/hec/hooks/useBuilder";
import {
  GUEST_LESSON_LIMIT,
  getVisitedLessons,
  isVisited,
  trackLessonVisit,
} from "~/hooks/learning-center/guest-lesson-tracker";
import { cn } from "~/lib/utils";

import { quizStatuses } from "./constants";
import CtaSection from "./cta-section";
import FailedQuizBlock from "./failed-quiz-block";
import LessonContentPreview from "./lesson-content-preview";
import messages from "./messages";
import PassedQuizBlock from "./passed-quiz-block";
import QuizForm from "./quiz-form";
import type {
  ModuleLesson,
  UseLearningCenterModuleReturn,
} from "./use-learning-center-module";

/**
 * SFCC-11505 — port of composable `moduleAccordion.jsx`. The single-open
 * accordion of lessons + quiz that drives the module player.
 *
 * Deltas vs composable:
 *  - `AccordionContent` (CSS-animated) replaces `DelayedAccordionContent`.
 *  - Scroll/hash sync uses `scrollIntoView` + `history.replaceState` (no
 *    `react-scroll`/`useMediaQuery`); items carry `scroll-mt-*` for the sticky
 *    header offset, avoiding the hydration flash the levels block also avoided.
 *  - Toasts use `react-hot-toast` (`toast(...)`), the app's toast system.
 *  - Per-lesson `disabled`/Lock is dropped (the Workers module view-model has no
 *    such flag); the quiz lock (until all lessons complete) is preserved.
 */
interface ModuleAccordionProps {
  moduleData: UseLearningCenterModuleReturn;
}

const ModuleAccordion = ({ moduleData }: ModuleAccordionProps) => {
  const {
    module,
    requestInProgress,
    submitLesson,
    refetchModule,
    submitQuiz,
    activeAccordionItem,
    setSelectedAccordionItem,
    quizStatus,
    setQuizStatus,
    goToNextModule,
    nextModuleData,
    isNextReady,
    ctaType,
    isGuest,
    isLevelComplete,
    setActiveLesson,
  } = moduleData;

  const { config } = useBuilder();
  const intl = useIntl();
  const autoSubmittedIdsRef = useRef<Set<string>>(new Set());
  const didInitRef = useRef(false);

  const scrollToItem = (id: string | null) => {
    if (!id || typeof window === "undefined") return;
    // pushState/replaceState do NOT emit `hashchange`, so this won't loop with
    // the listener below (which only fires on real hash navigation / back-fwd).
    window.history.replaceState(null, "", `#${id}`);
    requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Sync selection to real hash navigation (in-page #links, back/forward).
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        setSelectedAccordionItem(hash);
        scrollToItem(hash);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track guest visits as the open item changes (kept out of render).
  useEffect(() => {
    if (isGuest && activeAccordionItem) trackLessonVisit(activeAccordionItem);
  }, [isGuest, activeAccordionItem]);

  // First-ready init: pin the open item, then mark the opened lesson complete
  // once without advancing. For guests this seeds the structured
  // `guestLearningCenterData` tree (level/module start + per-lesson complete) so
  // the Goal Based Learning section reflects progress — gated by the 2-lesson
  // guest cap, same as `trackLesson`. Runs when module + open id arrive.
  useEffect(() => {
    if (didInitRef.current) return;
    const openedId = activeAccordionItem;
    const lessons = module?.lessons;
    if (!openedId || !lessons?.length) return;
    didInitRef.current = true;

    setSelectedAccordionItem(openedId);

    const openedLesson = lessons.find(l => l.id === openedId);
    if (!openedLesson || openedLesson.complete || !openedLesson.id) return;
    // Guests: only seed within the 2-lesson cap (and don't burn a visit slot the
    // user hasn't taken — only seed lessons already visited or with room left).
    if (isGuest && getVisitedLessons().length >= GUEST_LESSON_LIMIT) return;
    const openedIndex = lessons.findIndex(l => l.id === openedId);
    autoSubmittedIdsRef.current.add(openedLesson.id);
    void submitLesson(openedLesson, lessons[openedIndex + 1], {
      markOnly: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, activeAccordionItem]);

  if (!module) return null;

  const trackLesson = (lesson: ModuleLesson | undefined, index?: number) => {
    if (!lesson) return;
    if (isGuest && getVisitedLessons().length < GUEST_LESSON_LIMIT) {
      if (lesson.id) trackLessonVisit(lesson.id);
      void setActiveLesson(window.location.pathname + window.location.hash);
      // Persist guest progress into the structured tree (composable parity):
      // mark this lesson complete + seed its level/module start, so the goal
      // banner / levels blocks (which count from the structured tree) reflect
      // the guest's started/completed lessons. `markOnly` does not advance the
      // accordion. Skipped once already auto-submitted (init or a prior visit).
      if (
        lesson.id &&
        !lesson.complete &&
        !autoSubmittedIdsRef.current.has(lesson.id)
      ) {
        autoSubmittedIdsRef.current.add(lesson.id);
        const nextLesson =
          typeof index === "number" ? module.lessons[index + 1] : undefined;
        void submitLesson(lesson, nextLesson, { markOnly: true });
      }
    } else if (isGuest) {
      toast(intl.formatMessage(messages.guestLessonLimitReached));
    }
  };

  const numOfLessons = module.lessons.length;

  const renderLessons = () =>
    module.lessons.map((lesson, i, lessons) => {
      const visited = lesson.id ? isVisited(lesson.id) : false;
      const itemValue = lesson.id ?? `lesson-${i}`;
      return (
        <AccordionItem
          value={itemValue}
          key={itemValue}
          id={itemValue}
          className={cn(
            "scroll-mt-20 border-b-0 lg:scroll-mt-24",
            i > 0 && "border-t",
          )}
          data-testid="moduleAccordion-AccordionItem"
        >
          <AccordionTrigger
            className="body-header-lg theme-medium px-2 pb-[0.8125rem] pt-[1.1875rem] text-left text-accent lg:px-6 [&[data-state=open]]:bg-background/30"
            onClick={() => {
              trackLesson(lesson, i);
              // Registered: mark on expand (non-advancing), once per lesson.
              if (
                !isGuest &&
                lesson.id &&
                !autoSubmittedIdsRef.current.has(lesson.id) &&
                lesson.complete === false
              ) {
                autoSubmittedIdsRef.current.add(lesson.id);
                void submitLesson(lesson, undefined, { markOnly: true });
              }
            }}
            data-testid="moduleAccordion-AccordionTrigger"
          >
            <div className="mr-2 flex flex-1 justify-between">
              {lesson.title ?? ""}
              {isGuest && (
                <Badge variant={visited ? "default" : "outline"}>
                  {visited
                    ? intl.formatMessage(messages.lessonCompleted)
                    : intl.formatMessage(messages.lessonGetStarted)}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent
            className="flex flex-col px-2 pb-8 pt-4 lg:px-6 lg:pt-8"
            data-testid="moduleAccordion-AccordionContent"
          >
            <LessonContentPreview
              content={lesson.content}
              builderModel={ModelMap.learningCenterLessonContent}
              showFull={!isGuest || visited}
            />
            {isGuest && !visited ? (
              <a
                href="#login"
                className="inline"
                data-testid="moduleAccordion-LoginLink"
                onClick={() => trackLesson(lesson, i)}
              >
                <Button
                  type="button"
                  variant="cta"
                  className="w-full lg:ml-auto lg:w-[14.1875rem]"
                  disabled={requestInProgress || i === numOfLessons - 1}
                  data-testid="moduleAccordion-Button"
                >
                  {intl.formatMessage(messages.signInToViewMore)}
                </Button>
              </a>
            ) : (
              <Button
                type="button"
                variant="cta"
                className="w-full lg:ml-auto lg:w-[14.1875rem]"
                onClick={async () => {
                  trackLesson(lessons[i + 1], i + 1);
                  try {
                    await submitLesson(lesson, lessons[i + 1]);
                    // Continue: Quiz (last lesson) — completing it flips
                    // allLessonsComplete true and opens the quiz. The module
                    // (quiz inlined) is fetched once and never auto-revalidates
                    // (resource shouldRevalidate=false, SFCC-11733), so an
                    // intermittently-blank quiz would otherwise stay blank; force
                    // a module refetch on this deliberate transition so the quiz
                    // form is fresh. (SFCC-11738.)
                    if (numOfLessons - 1 === i) refetchModule();
                    // Registered writes are server-confirmed → toast the result.
                    // Guests persist to localStorage synchronously and don't
                    // toast (composable parity).
                    if (!isGuest) {
                      toast.success(
                        intl.formatMessage(messages.lessonProgressSaved),
                      );
                    }
                  } catch {
                    if (!isGuest) {
                      toast.error(
                        intl.formatMessage(messages.lessonProgressSaveError),
                      );
                    }
                  }
                }}
                disabled={
                  requestInProgress ||
                  (!module.shouldQuizButtonBeEnabled && i === numOfLessons - 1)
                }
                data-testid="moduleAccordion-Button"
              >
                {numOfLessons - 1 === i
                  ? intl.formatMessage(messages.continueToQuiz)
                  : intl.formatMessage(messages.continueToLesson, {
                      lessonNumber: i + 2,
                    })}
              </Button>
            )}
          </AccordionContent>
        </AccordionItem>
      );
    });

  const renderQuiz = () => {
    const quiz = module.quiz;
    if (!quiz?.id) return null;
    const disabled = !module.allLessonsComplete;
    // The module summary is a Builder content reference (the view-model types it
    // loosely). Resolve `.value` when wrapped, and ONLY pass an object to
    // <Content> — a string/primitive (per the loose type) must never reach it.
    const summaryRaw = module.summary as unknown;
    const summaryResolved =
      summaryRaw && typeof summaryRaw === "object" && "value" in summaryRaw
        ? (summaryRaw as { value?: unknown }).value
        : summaryRaw;
    const summaryContent =
      summaryResolved && typeof summaryResolved === "object"
        ? (summaryResolved as BuilderContent)
        : undefined;

    return (
      <AccordionItem
        value={quiz.id}
        key={quiz.id}
        // Locked (lessons incomplete) → keep the quiz un-openable, matching
        // composable. Without this the trigger still toggles: it opens to an
        // empty panel (content is gated on `allLessonsComplete`) and the Lock
        // icon rotates on open. Flips openable once `allLessonsComplete`.
        disabled={disabled}
        className="scroll-mt-20 border-b-0 border-t lg:scroll-mt-24"
        id={quiz.id}
        data-testid="moduleAccordion-AccordionItem"
      >
        <AccordionTrigger
          icon={disabled ? <Lock /> : undefined}
          className={cn(
            "body-header-lg theme-medium px-2 pb-[0.8125rem] pt-[1.1875rem] text-left text-accent lg:px-6 [&[data-state=open]]:bg-background/30",
            disabled && "hover:no-underline",
          )}
          data-testid="moduleAccordion-AccordionTrigger"
        >
          {intl.formatMessage(messages.quiz)}
        </AccordionTrigger>
        <AccordionContent
          className="flex flex-col pb-8 pt-4 lg:pr-12 lg:pt-8"
          data-testid="moduleAccordion-AccordionContent"
        >
          {module.allLessonsComplete && (
            <>
              {summaryContent && (
                <div className="theme-medium mb-11 rounded-md bg-background/30 px-2 py-6 lg:mb-[3.25rem] lg:px-6">
                  <Content
                    model={ModelMap.learningCenterModuleSummary}
                    content={summaryContent}
                    apiKey={config.api_key}
                    customComponents={customComponents}
                    linkComponent={GuardedBuilderLink}
                    data-testid="moduleAccordion-Summary"
                  />
                </div>
              )}
              {quizStatus === quizStatuses.OPEN && (
                <div className="px-2 lg:px-6">
                  <QuizForm
                    module={module}
                    requestInProgress={requestInProgress}
                    onSubmit={data => {
                      // A failed persist must surface, not vanish (COM-588).
                      submitQuiz(data).catch(() => {
                        toast.error(intl.formatMessage(messages.quizSaveError));
                      });
                      if (quiz.id) scrollToItem(quiz.id);
                    }}
                  />
                </div>
              )}
              {quizStatus === quizStatuses.PASSED && (
                <PassedQuizBlock moduleTitle={module.title} />
              )}
              {quizStatus === quizStatuses.FAILED && <FailedQuizBlock />}
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={activeAccordionItem ?? ""}
      onValueChange={value => setSelectedAccordionItem(value || null)}
      data-testid="moduleAccordion"
    >
      {renderLessons()}
      {renderQuiz()}
      {(quizStatus === quizStatuses.FAILED ||
        quizStatus === quizStatuses.PASSED) && (
        <CtaSection
          quizStatus={quizStatus}
          setQuizStatus={setQuizStatus}
          requestInProgress={requestInProgress}
          goToNextModule={goToNextModule}
          nextModuleData={nextModuleData}
          isNextReady={isNextReady}
          ctaType={ctaType}
          isLevelComplete={isLevelComplete}
        />
      )}
    </Accordion>
  );
};

export default ModuleAccordion;
